import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Header';

import './NxlStats.css';

import {
  PUBLIC_MARKET_API,
  fetchJson,
} from './nxlApi';

import {
  marketMetaLine,
  marketTitle,
  percent,
  scheduleLabel,
} from './nxlUtils';

function getScheduleTime(row) {
  if (row?.starts_at) {
    const t = new Date(row.starts_at).getTime();
    if (Number.isFinite(t)) return t;
  }

  return Number.MAX_SAFE_INTEGER;
}

function getDateKey(row) {
  if (row?.starts_at) {
    const d = new Date(row.starts_at);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    }
  }

  return row?.date_label || 'Schedule TBA';
}

function probabilityValue(team) {
  const value = Number(team?.probability);
  return Number.isFinite(value) ? value : 0;
}

function ProbabilitySide({ team, side }) {
  const probability = probabilityValue(team);
  const width = `${Math.max(0, Math.min(100, probability * 100))}%`;

  return (
    <div className={`nxl-schedule-team ${side}`}>
      <div className="nxl-schedule-team-top">
        <strong>{team?.name || 'Team TBD'}</strong>
        <span>{percent(team?.probability)}</span>
      </div>
      <div className="nxl-schedule-prob-track" aria-hidden="true">
        <div className="nxl-schedule-prob-fill" style={{ width }} />
      </div>
    </div>
  );
}

function ScheduleCard({ match }) {
  const marketLike = {
    ...match,
    _id: match.market_id,
    team_a: match.team_a?.name,
    team_b: match.team_b?.name,
  };

  return (
    <article className="nxl-schedule-card">
      <div className="nxl-schedule-card-main">
        <div>
          <p className="nxl-schedule-time">{scheduleLabel(match)}</p>
          <h3>{marketTitle(marketLike)}</h3>
          <small className="nxl-market-meta">
            {match.division}
            {marketMetaLine(match) ? ` - ${marketMetaLine(match)}` : ''}
          </small>
        </div>

        <span className={`nxl-schedule-status ${String(match.status || '').toLowerCase()}`}>
          {match.status || 'scheduled'}
        </span>
      </div>

      <div className="nxl-schedule-prob-grid">
        <ProbabilitySide team={match.team_a} side="a" />
        <ProbabilitySide team={match.team_b} side="b" />
      </div>
    </article>
  );
}

function NxlSchedule() {
  const [schedule, setSchedule] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [division, setDivision] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [status, setStatus] = useState('all');
  const [from, setFrom] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadDivisions() {
      try {
        const data = await fetchJson(`${PUBLIC_MARKET_API}/divisions`);
        if (!cancelled) setDivisions(data.divisions || []);
      } catch (err) {
        console.warn('[NXL schedule divisions failed]', err);
      }
    }

    loadDivisions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');

        const params = new URLSearchParams({
          status,
          from,
          limit: '2000',
        });

        if (division) params.set('division', division);
        if (teamFilter.trim()) params.set('team', teamFilter.trim());

        const data = await fetchJson(`${PUBLIC_MARKET_API}/schedule?${params.toString()}`);
        const rows = [...(data.schedule || [])].sort((a, b) => {
          const timeDiff = getScheduleTime(a) - getScheduleTime(b);
          if (timeDiff !== 0) return timeDiff;
          return String(a.field || '').localeCompare(String(b.field || ''));
        });

        if (!cancelled) {
          setSchedule(rows);
          setLastUpdated(new Date());
        }
      } catch (err) {
        console.error('[NXL schedule load failed]', err);
        if (!cancelled) setError(err.message || 'Failed to load NXL schedule.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [division, teamFilter, status, from]);

  const groupedSchedule = useMemo(() => {
    return schedule.reduce((groups, match) => {
      const key = getDateKey(match);
      if (!groups[key]) groups[key] = [];
      groups[key].push(match);
      return groups;
    }, {});
  }, [schedule]);

  const dayKeys = Object.keys(groupedSchedule);

  return (
    <div className="nxl-page nxl-schedule-page">
      <Header />

      <main className="nxl-shell nxl-schedule-shell">
        <section className="nxl-hero nxl-schedule-hero">
          <p className="nxl-kicker">NXL SCHEDULE</p>
          <h1>Match times with model prediction percentages.</h1>
          <p>
            A time-sorted view of scheduled NXL matchups using model win probability,
            not Vegas or payout odds.
          </p>

          <div className="nxl-live-row">
            <span className="nxl-live-pill">{schedule.length} matches</span>
            {lastUpdated && (
              <span>
                Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
        </section>

        <section className="nxl-panel nxl-schedule-filters">
          <label className="nxl-field">
            <span>Division</span>
            <select value={division} onChange={(e) => setDivision(e.target.value)}>
              <option value="">All divisions</option>
              {divisions.map((row) => (
                <option key={row.division} value={row.division}>
                  {row.division}
                </option>
              ))}
            </select>
          </label>

          <label className="nxl-field">
            <span>Team</span>
            <input
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              placeholder="Search team..."
            />
          </label>

          <label className="nxl-field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="locked">Locked</option>
              <option value="settled">Settled</option>
              <option value="void">Void</option>
            </select>
          </label>

          <label className="nxl-field">
            <span>Window</span>
            <select value={from} onChange={(e) => setFrom(e.target.value)}>
              <option value="all">Full schedule</option>
              <option value="now">Upcoming only</option>
            </select>
          </label>
        </section>

        {error && <div className="nxl-error">{error}</div>}

        {loading ? (
          <section className="nxl-panel">Loading NXL schedule...</section>
        ) : (
          <section className="nxl-schedule-days">
            {dayKeys.map((day) => (
              <div className="nxl-schedule-day" key={day}>
                <div className="nxl-schedule-day-heading">
                  <h2>{day}</h2>
                  <span>{groupedSchedule[day].length} matches</span>
                </div>

                <div className="nxl-schedule-list">
                  {groupedSchedule[day].map((match) => (
                    <ScheduleCard key={match.market_id || match.match_id} match={match} />
                  ))}
                </div>
              </div>
            ))}

            {!dayKeys.length && (
              <section className="nxl-panel">
                <h2>No scheduled matches found</h2>
                <p className="nxl-muted">Try a different division, team, status, or window.</p>
              </section>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default NxlSchedule;
